import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  ModalIsland,
  Typography,
} from "@saviynt/design-system";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useUpdateSaveFilterView } from "../../../../hooks/usePolicySaveFilterView";
import {
  errorMessages,
  VIEW_NAME_REGEX,
} from "../../../../constants/filterSaveView.constants";
import { parseError } from "../../../PoliciesFindingsList/utils";

interface RenameViewModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  viewData: {
    name: string;
    id: number;
  };
}

export default function RenameViewModal({
  isOpen,
  setIsOpen,
  viewData,
}: RenameViewModalProps) {
  const intl = useIntl();
  const closeModal = useCallback(() => setIsOpen(false), [setIsOpen]);
  const { mutate } = useUpdateSaveFilterView({
    onSuccess: () => closeModal(),
    onError: (error: unknown) => {
      console.log("====Error in Update view", parseError(error).message);
    },
  });

  const stripDefault = (name: string) => name.replace(/ \(default\)$/, "");

  const [viewName, setViewName] = useState(() => stripDefault(viewData.name));

  useEffect(() => {
    setViewName(stripDefault(viewData.name));
  }, [viewData.name]);
  const [error, setError] = useState("");

  const validateAndSetError = (name: string): boolean => {
    if (!name) {
      setError(intl.formatMessage(errorMessages.MIN_CHAR));
      return false;
    }
    if (!VIEW_NAME_REGEX.test(name)) {
      setError(intl.formatMessage(errorMessages.NO_SPECIAL_CHAR_ALLOWED));
      return false;
    }
    setError("");
    return true;
  };

  const handleError = useCallback((error: unknown) => {
    const { message, code = "" } = parseError(error);
    const errorMessage =
      code in errorMessages
        ? intl.formatMessage(errorMessages[code as keyof typeof errorMessages])
        : message;
    setError(
      errorMessage === "View with same name already exists"
        ? intl.formatMessage(errorMessages.DUPLICATE_ITEM)
        : errorMessage,
    );
  }, []);

  const handleSubmit = useCallback(() => {
    if (!validateAndSetError(viewName)) return;

    mutate({ view_name: viewName, id: viewData.id });
  }, [viewName, viewData.id, mutate, handleError]);

  const handleInputChange = useCallback(
    (value: string) => {
      setViewName(value);
      if (error) setError("");
    },
    [error],
  );

  return (
    <ModalIsland
      onClose={closeModal}
      isOpen={isOpen}
      title={intl.formatMessage({
        description:
          "Modal title for renaming an existing saved NHI inventory view",
        defaultMessage: "Rename View",
        id: "3+TxV0",
      })}
      contentBody={
        <Box>
          <InputField
            name="viewName"
            isRequired
            placeholder={intl.formatMessage({
              description:
                "Placeholder text for the view name input field in the NHI rename view modal",
              defaultMessage: "Provide a name for this view",
              id: "isdwsQ",
            })}
            label={
              <Typography kind="label">
                {intl.formatMessage({
                  description:
                    "Label for the view name input field in the NHI rename view modal",
                  defaultMessage: "View Name",
                  id: "p6Czpi",
                })}
              </Typography>
            }
            type="text"
            value={viewName}
            setValue={handleInputChange}
            isCritical={!!error}
            setIsCritical={() => setError("")}
            CriticalHelperText={
              error && (
                <InlineMessage
                  text={error}
                  colorTheme="critical"
                  size="small"
                  leftIcon={<Icon kind="AlertCriticalFilled" />}
                />
              )
            }
          />
        </Box>
      }
      FooterActionBarComp={
        <FooterActionBar size="large">
          <div className="flex pl-2 gap-4 ml-auto">
            <Button kind="outlined" size="medium" onClick={closeModal}>
              {intl.formatMessage({
                id: "RbfuXN",
                defaultMessage: "Cancel",
                description:
                  "Button text to cancel saving the policies view and close the modal",
              })}
            </Button>
            <Button kind="filled" size="medium" onClick={handleSubmit}>
              {intl.formatMessage({
                defaultMessage: "Save",
                id: "wSRuxR",
                description:
                  "Button text to save the policies view with the specified name and preferences",
              })}
            </Button>
          </div>
        </FooterActionBar>
      }
    />
  );
}
